(() => {
  if (window.__smoothElementRevealV1) return;

  window.__smoothElementRevealV1 = true;

  const SECTION_SELECTOR =
    'main .shopify-section';

  const ELEMENT_SELECTOR = [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'p',
    'blockquote',
    'li',
    '.button',
    'button:not(.slider-button)',
    '.price',
    '.subtitle',
    '.caption',
    '.caption-with-letter-spacing',
    '.card__heading',
    '.article-card__excerpt',
    '.product__text',
    'img',
    'video',
    'iframe',
    'model-viewer'
  ].join(',');

  const EXCLUDED_SELECTOR = [
    '[data-scroll-reveal="off"]',
    '[hidden]',
    '[aria-hidden="true"]',
    '.visually-hidden',
    '.skip-to-content-link',
    '.product-media-modal',
    '[data-marcha-direct-modal]',
    '.quick-add-modal',
    '.quick-add-modal__content',
    '.drawer',
    '.predictive-search',
    'script',
    'style',
    'template',
    'noscript'
  ].join(',');

  const ITEM_CLASS =
    'scroll-reveal-item';

  const PENDING_CLASS =
    'scroll-reveal-pending';

  const VISIBLE_CLASS =
    'scroll-reveal-visible';

  const preparedElements =
    new WeakSet();

  const reducedMotion =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

  let observer = null;
  let mutationFrame = null;
  let queuedRoots = [];

  const isElementRendered = (
    element
  ) => {
    const styles =
      window.getComputedStyle(element);

    if (
      styles.display === 'none' ||
      styles.visibility === 'hidden'
    ) {
      return false;
    }

    const rect =
      element.getBoundingClientRect();

    return (
      rect.width > 1 &&
      rect.height > 1
    );
  };

  const isExcluded = (element) => {
    return Boolean(
      element.closest(
        EXCLUDED_SELECTOR
      )
    );
  };

  const containsSeparateTargets = (
    element
  ) => {
    /*
     * Prevent large card/list wrappers from fading together
     * when their image and text will fade separately.
     */
    if (
      !element.matches(
        'li, blockquote'
      )
    ) {
      return false;
    }

    return Boolean(
      element.querySelector(
        [
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
          'p',
          'img',
          'video',
          'iframe',
          'model-viewer',
          '.button',
          '.price'
        ].join(',')
      )
    );
  };

  const isInitiallyVisible = (
    element
  ) => {
    const rect =
      element.getBoundingClientRect();

    return (
      rect.bottom > 0 &&
      rect.top <
        window.innerHeight * 0.93
    );
  };

  const finishReveal = (
    element
  ) => {
    if (
      element.dataset
        .scrollRevealFinished ===
      'true'
    ) {
      return;
    }

    element.dataset
      .scrollRevealFinished =
      'true';

    requestAnimationFrame(() => {
      element.classList.remove(
        PENDING_CLASS
      );

      element.classList.add(
        VISIBLE_CLASS
      );
    });
  };

  const revealImage = (image) => {
    let finished = false;

    const complete = () => {
      if (finished) return;

      finished = true;

      window.clearTimeout(
        image.__scrollRevealTimer
      );

      finishReveal(image);
    };

    /*
     * Wait for the actual image to load before fading it in.
     * This creates the lazy-load-style appearance.
     */
    if (
      image.complete &&
      image.naturalWidth > 0
    ) {
      if (
        typeof image.decode ===
        'function'
      ) {
        image
          .decode()
          .catch(() => undefined)
          .finally(complete);
      } else {
        complete();
      }

      return;
    }

    image.addEventListener(
      'load',
      complete,
      {
        once: true
      }
    );

    image.addEventListener(
      'error',
      complete,
      {
        once: true
      }
    );

    /*
     * Safety fallback so a broken image can never remain hidden.
     */
    image.__scrollRevealTimer =
      window.setTimeout(
        complete,
        2500
      );
  };

  const revealElement = (
    element
  ) => {
    observer?.unobserve(element);

    if (
      element.tagName === 'IMG'
    ) {
      revealImage(element);
      return;
    }

    finishReveal(element);
  };

  const createObserver = () => {
    if (
      reducedMotion.matches ||
      !(
        'IntersectionObserver' in
        window
      )
    ) {
      return null;
    }

    return new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          revealElement(
            entry.target
          );
        });
      },
      {
        root: null,

        /*
         * Reveal shortly after the element enters the lower
         * portion of the screen.
         */
        rootMargin:
          '0px 0px -8% 0px',

        threshold: 0.04
      }
    );
  };

  const prepareElement = (
    element
  ) => {
    if (
      !(
        element instanceof
        HTMLElement
      ) ||
      preparedElements.has(element)
    ) {
      return;
    }

    if (
      isExcluded(element) ||
      containsSeparateTargets(element) ||
      !isElementRendered(element)
    ) {
      return;
    }

    preparedElements.add(element);

    element.classList.add(
      ITEM_CLASS
    );

    /*
     * Do not hide content already visible when the page loads.
     * This prevents the hero and first screen from flashing.
     */
    if (
      reducedMotion.matches ||
      !observer ||
      isInitiallyVisible(element)
    ) {
      finishReveal(element);
      return;
    }

    element.classList.add(
      PENDING_CLASS
    );

    element.classList.remove(
      VISIBLE_CLASS
    );

    /*
     * Waiting one frame ensures opacity: 0 is painted before
     * IntersectionObserver is allowed to reveal the element.
     */
    requestAnimationFrame(() => {
      if (
        element.isConnected &&
        element.classList.contains(
          PENDING_CLASS
        )
      ) {
        observer.observe(element);
      }
    });
  };

  const prepareSection = (
    section
  ) => {
    if (
      !(
        section instanceof
        HTMLElement
      )
    ) {
      return;
    }

    const elements =
      section.querySelectorAll(
        ELEMENT_SELECTOR
      );

    elements.forEach(
      prepareElement
    );
  };

  const prepareRoot = (
    root = document
  ) => {
    if (
      root instanceof HTMLElement &&
      root.matches(
        SECTION_SELECTOR
      )
    ) {
      prepareSection(root);
    }

    root
      .querySelectorAll?.(
        SECTION_SELECTOR
      )
      .forEach(prepareSection);
  };

  const queueRoot = (root) => {
    if (!root) return;

    queuedRoots.push(root);

    if (mutationFrame) return;

    mutationFrame =
      requestAnimationFrame(() => {
        const roots =
          Array.from(
            new Set(queuedRoots)
          );

        queuedRoots = [];
        mutationFrame = null;

        roots.forEach(
          prepareRoot
        );
      });
  };

  const initialize = () => {
    document.documentElement
      .classList.add(
        'smooth-scroll-reveal-enabled'
      );

    observer =
      createObserver();

    prepareRoot(document);

    /*
     * Handles product recommendations, app blocks,
     * AJAX-rendered sections and other dynamically added content.
     */
    const mutationObserver =
      new MutationObserver(
        (mutations) => {
          mutations.forEach(
            (mutation) => {
              mutation.addedNodes.forEach(
                (node) => {
                  if (
                    node instanceof
                    HTMLElement
                  ) {
                    queueRoot(node);
                  }
                }
              );
            }
          );
        }
      );

    mutationObserver.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  };

  /*
   * Shopify Theme Customizer support.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      queueRoot(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:select',
    (event) => {
      const section =
        event.target.matches?.(
          SECTION_SELECTOR
        )
          ? event.target
          : event.target.querySelector?.(
              SECTION_SELECTOR
            );

      if (!section) return;

      section
        .querySelectorAll(
          `.${PENDING_CLASS}`
        )
        .forEach(revealElement);
    }
  );

  reducedMotion.addEventListener?.(
    'change',
    () => {
      if (!reducedMotion.matches) {
        return;
      }

      observer?.disconnect();

      document
        .querySelectorAll(
          `.${PENDING_CLASS}`
        )
        .forEach(revealElement);
    }
  );

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      {
        once: true
      }
    );
  } else {
    initialize();
  }
})();